import { IconPencil, IconTrash } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useState } from 'react';
import classNames from 'classnames';
import { NSCard } from 'bricks';
import RollupItem from 'components/RollupItem/RollupItem';
import { IOptionAnyValue } from 'bricks/types';
import { AttributeKey } from 'views/Reports/types';
import { IDealAttribute } from 'components/DealForm/types';
import { getFormattedFilterValue, getFormattedRangeValue } from './helpers';
import { IFilter } from './types';
import FilterModal from './FilterModal/FilterModal';
import { Attributes } from '../constants';

interface FilterProps {
    filter: IFilter;
    editFilter: (attribute: AttributeKey, filter: IFilter, unitType?: string) => void;
    removeFilter: (attribute: AttributeKey, unitType?: string) => void;
    attributeOptions: IOptionAnyValue[];
    customAttributes?: IDealAttribute[];
    unitTypeOptions: IOptionAnyValue[];
}

const Filter = ({
    filter, editFilter, removeFilter, attributeOptions, customAttributes, unitTypeOptions,
}: FilterProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isEditFilterModalShown, setIsEditFilterModalShown] = useState(false);
    const {
        attribute, filterOption, fieldInput, unitType,
    } = filter;

    let filterAttribute: string = attribute;
    if (unitType) filterAttribute += `: ${unitType}`;

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    const handleEditFilter = (updatedFilter: IFilter) => editFilter(attribute, updatedFilter, unitType);
    const handleRemoveFilter = () => removeFilter(attribute, unitType);

    return (
        <NSCard className={classNames(['NSCard--level-4 p-2 my-1 Filter', { 'NSCard--level-5': isHovered }])}>
            <div className="d-flex justify-content-between align-items-center" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <div>
                    <h5 className="text-white m-0 font-weight-bold">{filterAttribute}</h5>
                    <span className="text-nowrap">{filterOption}</span>
                </div>
                {!isHovered ? (
                    <div className="text-white text-right text-truncate text-wrap ml-2">
                        {fieldInput && 'value' in fieldInput && (
                            <h5 className="text-white">{getFormattedFilterValue(attribute as Attributes, fieldInput.value)}</h5>
                        )}
                        {fieldInput && 'to' in fieldInput && 'from' in fieldInput && (
                            <h5>{getFormattedRangeValue(attribute as Attributes, fieldInput)}</h5>
                        )}
                        {fieldInput && 'options' in fieldInput && (
                            <RollupItem
                                id={`${filterAttribute.split(' ').join('-')}-selections`}
                                values={fieldInput.options.map(o => o.label)}
                                newlineSeparated
                            />
                        )}
                    </div>
                ) : (
                    <div className="text-white">
                        <NSIcon icon={IconPencil} size="lg" className="cursor--pointer" onClick={() => setIsEditFilterModalShown(true)} />
                        <NSIcon icon={IconTrash} size="lg" className="ml-3 cursor--pointer" onClick={handleRemoveFilter} />
                    </div>
                )}
            </div>
            <FilterModal
                filter={filter}
                toggle={() => setIsEditFilterModalShown(!isEditFilterModalShown)}
                isOpen={isEditFilterModalShown}
                saveFilter={handleEditFilter}
                attributeOptions={attributeOptions}
                customAttributes={customAttributes}
                unitTypeOptions={unitTypeOptions}
            />
        </NSCard>
    );
};

export default Filter;
